import type { EntryType } from './types';

export type ObservePerformance = Omit<PerformanceObserver, 'observe'>;

export function supportPerformanceTimingEvent(entryType: string): boolean {
    return (
        window.PerformanceObserver &&
        PerformanceObserver.supportedEntryTypes !== undefined &&
        PerformanceObserver.supportedEntryTypes.includes(entryType)
    );
}

/**
 * Creates and returns a PerformanceObserver that monitors specified performance entry types.
 *
 * @param {EntryType[]} type - An array of performance entry types to observe.
 * @param {function(function(): void, PerformanceEntry): void} callback - A function called for each performance entry.
 *        The callback receives two parameters:
 *        - A function to disconnect the observer.
 *        - The performance entry object.
 *
 * @returns {ObservePerformance} A PerformanceObserver object.
 *
 * @example
 * observePerformance(['paint', 'resource'], (disconnect, entry) => {
 *   console.log(`Performance entry: ${entry.name}, Type: ${entry.entryType}, Start time: ${entry.startTime}`);
 *   if (entry.name === 'first-contentful-paint') {
 *     console.log('First Contentful Paint observed');
 *     disconnect(); // Stop observing after FCP
 *   }
 * });
 *
 * @remarks
 * - This function creates a PerformanceObserver that continues to observe and call the callback
 *   for each matching performance entry until explicitly disconnected.
 * - The callback function is provided with a `disconnect` function,
 *   allowing for easy cleanup.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/PerformanceObserver|PerformanceObserver MDN Documentation}
 */
export function observePerformance(
    type: EntryType[],
    callback: (disconnect: () => void, entry: PerformanceEntry) => void,
): ObservePerformance {
    if (type.some((entryType) => !supportPerformanceTimingEvent(entryType))) {
        // avoiding crash for very old browsers which dont support the given entry type.
        return {
            disconnect() {},
            takeRecords() {
                return [];
            },
        };
    }

    const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
            callback(() => observer.disconnect(), entry);
        });
    });

    observer.observe({ entryTypes: type });

    return observer;
}
